import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  CompensationLine,
  CreateRolePayload,
  RoleAcceptPosition,
  RoleDetail,
  RoleOptionItem,
  UpdateRolePayload,
  useCreateRoleMutation,
  useGetRoleTagsQuery,
  useUpdateRoleMutation,
} from "../../../app/services/roles";
import { formatYen, toYenString } from "../../../utils/money";
import Modal from "../../../components/modal/Modal";
import IconButton from "../../../components/button/IconButton";
import Input from "../../../components/form-item/Input";
import NumberInput from "../../../components/form-item/NumberInput";
import MoneyInput from "../../../components/form-item/MoneyInput";
import Textarea from "../../../components/form-item/Textarea";
import Toggle from "../../../components/form-item/Toggle";
import TagSelector from "../../../components/tag-selector/TagSelector";

import RoleThumbnail from "./RoleThumbnail";
import FullWidthAddBlock from "./FullWidthAddBlock";

interface Props {
  mode: "create" | "edit";
  /** edit モードのときのみ。create では undefined */
  initialRole?: RoleDetail;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  role_name_jp: string;
  role_name_en: string;
  credit_title_jp: string;
  credit_title_en: string;
  description: string;
  accept_position: RoleAcceptPosition;
  allow_subcontracting: boolean;
  /** 整数文字列で扱い、保存時に parseInt する（`Input type="number"` と整合） */
  deadline_offset_days: string;
  deadline_time: string;
  valid_until_offset_days: string;
  valid_until_time: string;
  /** 空文字 = null。保存時に変換 */
  contract_start_offset_days: string;
  contract_end_offset_days: string;
  scope: string;
  initial_message: string;
  delivery_location: string;
  delivery_method: string;
  deliverable_format_options: RoleOptionItem[];
  special_provisions_options: RoleOptionItem[];
  compensation_lines: CompensationLine[];
  tags: number[];
  /**
   * is_active は UI からは編集対象外（常に既存値を維持、新規作成時は true）。
   * payload にだけ載せる目的で内部状態にだけ保持する。
   */
  is_active: boolean;
}

const DEFAULT_FORM: FormState = {
  role_name_jp: "",
  role_name_en: "",
  credit_title_jp: "",
  credit_title_en: "",
  description: "",
  accept_position: "SPECIALIST",
  allow_subcontracting: false,
  deadline_offset_days: "30",
  deadline_time: "18:00",
  valid_until_offset_days: "7",
  valid_until_time: "18:00",
  contract_start_offset_days: "0",
  contract_end_offset_days: "60",
  scope: "",
  initial_message: "",
  delivery_location: "",
  delivery_method: "",
  deliverable_format_options: [],
  special_provisions_options: [],
  compensation_lines: [],
  tags: [],
  is_active: true,
};

const fromRole = (role: RoleDetail): FormState => ({
  role_name_jp: role.role_name_jp,
  role_name_en: role.role_name_en,
  credit_title_jp: role.credit_title_jp,
  credit_title_en: role.credit_title_en,
  description: role.description,
  accept_position: role.accept_position,
  allow_subcontracting: role.allow_subcontracting,
  deadline_offset_days: String(role.deadline_offset_days),
  deadline_time: role.deadline_time,
  valid_until_offset_days: String(role.valid_until_offset_days),
  valid_until_time: role.valid_until_time,
  contract_start_offset_days:
    role.contract_start_offset_days === null
      ? ""
      : String(role.contract_start_offset_days),
  contract_end_offset_days:
    role.contract_end_offset_days === null
      ? ""
      : String(role.contract_end_offset_days),
  scope: role.scope,
  initial_message: role.initial_message,
  delivery_location: role.delivery_location,
  delivery_method: role.delivery_method,
  deliverable_format_options: [...role.deliverable_format_options],
  special_provisions_options: [...role.special_provisions_options],
  compensation_lines: role.compensation_lines.map((line) => ({
    ...line,
    // 円は整数のみ。API/DB 由来の小数文字列をここで整数へ正規化する。
    unit_price: toYenString(line.unit_price),
  })),
  tags: [...role.tags],
  is_active: role.is_active,
});

/** 編集対象の動的配列キー（`deliverable_format_options` / `special_provisions_options`） */
type OptionListKey = "deliverable_format_options" | "special_provisions_options";

const ThumbnailRow = styled.div.attrs({
  className: "u-row u-w100 u-gap12 u-align-center",
})``;

const Section = styled.section.attrs({ className: "u-col u-w100 u-gap12" })`
  border-top: 1px solid var(--soremo-border);
  padding-top: 16px;
`;

const SectionTitle = styled.h3.attrs({ className: "heading-13 u-w100" })`
  color: var(--soremo-light-gray);
`;

const FieldRow = styled.div.attrs({ className: "u-row u-w100 u-gap16" })`
  flex-wrap: wrap;
  align-items: flex-end;
`;

const FieldFull = styled.div.attrs({ className: "u-col u-gap4 u-w100" })``;

const RadioRow = styled.div.attrs({ className: "u-row u-gap16" })`
  flex-wrap: wrap;
`;

const RadioLabel = styled.label.attrs({ className: "u-row u-gap4 u-align-center" })`
  cursor: pointer;
`;

const ListRowGrow = styled.div.attrs({
  className: "u-row u-w100 u-gap8 u-align-end",
})``;

// 左ドラッグハンドル + 右ゴミ箱 の共通フォーマット
// （RoleTagsManager と同じスタイルセット）
const DragHandle = styled.div`
  flex-shrink: 0;
  color: var(--soremo-light-gray);
  display: flex;
  align-items: center;
  user-select: none;
`;

const ErrorText = styled.p.attrs({ className: "bodytext-11" })`
  color: var(--soremo-green);
`;

const Hint = styled.p.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const HalfField = styled.div`
  flex: 1 1 240px;
  min-width: 200px;
`;

const QuarterField = styled.div`
  flex: 1 1 140px;
  min-width: 120px;
`;

/**
 * 行追加 UI 用の sortable wrapper。
 * 左に drag_indicator（ドラッグハンドル）、右に delete アイコンを配置し、
 * 中央 children に各セクション固有の入力を置く。`RoleTagsManager` の
 * 行レイアウトと統一する。
 */
interface SortableRowProps {
  id: string;
  onDelete: () => void;
  children: React.ReactNode;
}

const SortableRow: React.FC<SortableRowProps> = ({
  id,
  onDelete,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    width: "100%",
  };
  return (
    <div ref={setNodeRef} style={style}>
      <ListRowGrow>
        <DragHandle
          {...attributes}
          {...listeners}
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
          aria-label="並び替えハンドル"
        >
          <span
            className="material-symbols-rounded"
            style={{ fontSize: 20 }}
          >
            drag_indicator
          </span>
        </DragHandle>
        {children}
        <IconButton onClick={onDelete} icon="delete" />
      </ListRowGrow>
    </div>
  );
};

const RoleEditModal: React.FC<Props> = ({
  mode,
  initialRole,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<FormState>(
    initialRole ? fromRole(initialRole) : DEFAULT_FORM,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: tagsData } = useGetRoleTagsQuery();
  const tagOptions = tagsData?.data?.tags ?? [];

  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();

  // すべての更新は functional update で書く。これにより useCallback の deps から
  // `form` を取り除け、不要な再生成を抑制できる。
  const update = useCallback(<K extends keyof FormState>(
    key: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const totalAmount = useMemo(() => {
    return form.compensation_lines.reduce((sum, line) => {
      const unit = Number.parseFloat(line.unit_price || "0") || 0;
      const qty = Number.parseFloat(line.quantity || "0") || 0;
      return sum + unit * qty;
    }, 0);
  }, [form.compensation_lines]);

  const addOption = useCallback((key: OptionListKey) => {
    setForm((prev) => ({ ...prev, [key]: [...prev[key], ""] }));
  }, []);

  const removeOption = useCallback((key: OptionListKey, index: number) => {
    setForm((prev) => {
      const next = prev[key].slice();
      next.splice(index, 1);
      return { ...prev, [key]: next };
    });
  }, []);

  const setOption = useCallback(
    (key: OptionListKey, index: number, value: string) => {
      setForm((prev) => {
        const next = prev[key].slice();
        next[index] = value;
        return { ...prev, [key]: next };
      });
    },
    [],
  );

  const addCompensationLine = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      compensation_lines: [
        ...prev.compensation_lines,
        { label: "", unit_price: "0", quantity: "1", unit: "" },
      ],
    }));
  }, []);

  const removeCompensationLine = useCallback((index: number) => {
    setForm((prev) => {
      const next = prev.compensation_lines.slice();
      next.splice(index, 1);
      return { ...prev, compensation_lines: next };
    });
  }, []);

  const setCompensationField = useCallback(
    (index: number, key: keyof CompensationLine, value: string) => {
      setForm((prev) => {
        const next = prev.compensation_lines.slice();
        next[index] = { ...next[index], [key]: value };
        return { ...prev, compensation_lines: next };
      });
    },
    [],
  );

  // ===== D&D 並び替え =====
  // 上下矢印で 1 つずつ動かす UI を廃止し、ドラッグハンドルで動かす UI に統一。
  // 共通の SortableRow が drag_indicator (左) と delete (右) を担当する。
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleOptionDragEnd = useCallback(
    (key: OptionListKey, event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      setForm((prev) => {
        const ids = prev[key].map((_, i) => `${key}-${i}`);
        const oldIndex = ids.indexOf(active.id as string);
        const newIndex = ids.indexOf(over.id as string);
        if (oldIndex === -1 || newIndex === -1) return prev;
        return { ...prev, [key]: arrayMove(prev[key], oldIndex, newIndex) };
      });
    },
    [],
  );

  const handleCompensationDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setForm((prev) => {
      const ids = prev.compensation_lines.map((_, i) => `comp-${i}`);
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return {
        ...prev,
        compensation_lines: arrayMove(
          prev.compensation_lines,
          oldIndex,
          newIndex,
        ),
      };
    });
  }, []);

  const validate = useCallback((): string | null => {
    if (form.role_name_jp.trim() === "") return "ロール名 (日本語) は必須です";
    const dl = Number.parseInt(form.deadline_offset_days || "0", 10);
    if (Number.isNaN(dl) || dl < 0)
      return "納期オフセットは 0 以上で指定してください";
    const vu = Number.parseInt(form.valid_until_offset_days || "0", 10);
    if (Number.isNaN(vu) || vu < 0)
      return "返答有効期限オフセットは 0 以上で指定してください";
    return null;
  }, [form]);

  const handleSubmit = useCallback(async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const payload: UpdateRolePayload = {
        role_name_jp: form.role_name_jp,
        role_name_en: form.role_name_en,
        credit_title_jp: form.credit_title_jp,
        credit_title_en: form.credit_title_en,
        description: form.description,
        accept_position: form.accept_position,
        allow_subcontracting: form.allow_subcontracting,
        deadline_offset_days: Number.parseInt(form.deadline_offset_days, 10) || 0,
        deadline_time: form.deadline_time,
        valid_until_offset_days:
          Number.parseInt(form.valid_until_offset_days, 10) || 0,
        valid_until_time: form.valid_until_time,
        contract_start_offset_days:
          form.contract_start_offset_days === ""
            ? null
            : Number.parseInt(form.contract_start_offset_days, 10),
        contract_end_offset_days:
          form.contract_end_offset_days === ""
            ? null
            : Number.parseInt(form.contract_end_offset_days, 10),
        scope: form.scope,
        initial_message: form.initial_message,
        delivery_location: form.delivery_location,
        delivery_method: form.delivery_method,
        deliverable_format_options: form.deliverable_format_options,
        special_provisions_options: form.special_provisions_options,
        compensation_lines: form.compensation_lines.map((l) => ({
          ...l,
          unit_price: toYenString(l.unit_price),
        })),
        tags: form.tags,
        is_active: form.is_active,
      };
      if (mode === "create") {
        await createRole(payload as CreateRolePayload).unwrap();
      } else if (initialRole) {
        await updateRole({ roleId: initialRole.id, payload }).unwrap();
      }
      onSaved();
      onClose();
    } catch (e) {
      console.error("Failed to save role:", e);
      setError("保存に失敗しました。入力内容を確認してください。");
    } finally {
      setSubmitting(false);
    }
  }, [
    form,
    mode,
    initialRole,
    createRole,
    updateRole,
    onSaved,
    onClose,
    validate,
  ]);

  return (
    <Modal
      align="top"
      onClose={submitting ? undefined : onClose}
      title={mode === "create" ? "ロールを追加" : "ロールを編集"}
      description={
        mode === "edit" && initialRole ? `#${initialRole.id}` : undefined
      }
      primaryAction={{
        label: submitting
          ? "保存中..."
          : mode === "create"
            ? "作成する"
            : "保存",
        onClick: () => void handleSubmit(),
        disabled: submitting,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
        disabled: submitting,
        theme: "tertiary",
        variant: "outlined",
      }}
    >
      <div className="u-col u-w100 u-gap16">
        {initialRole && (
          <ThumbnailRow>
            <RoleThumbnail roleId={initialRole.id} size={48} />
          </ThumbnailRow>
        )}

        <Section>
          <FieldRow>
            <HalfField>
              <Input
                label="ロール名 (日本語)"
                option="required"
                value={form.role_name_jp}
                onChange={(e) => update("role_name_jp", e.target.value)}
              />
            </HalfField>
            <HalfField>
              <Input
                label="ロール名 (英語)"
                option="optional"
                value={form.role_name_en}
                onChange={(e) => update("role_name_en", e.target.value)}
              />
            </HalfField>
          </FieldRow>
          <FieldRow>
            <HalfField>
              <Input
                label="クレジットタイトル (日本語)"
                option="optional"
                value={form.credit_title_jp}
                onChange={(e) => update("credit_title_jp", e.target.value)}
              />
            </HalfField>
            <HalfField>
              <Input
                label="クレジットタイトル (英語)"
                option="optional"
                value={form.credit_title_en}
                onChange={(e) => update("credit_title_en", e.target.value)}
              />
            </HalfField>
          </FieldRow>
          <FieldFull>
            <Textarea
              label="説明"
              option="optional"
              rows={3}
              value={form.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                update("description", e.target.value)
              }
            />
          </FieldFull>
          <FieldFull>
            <label className="bodytext-13 u-line-height-100 u-pl4">
              受託者ポジション (accept_position)
              <span className="label8 u-pl8 u-text-blue">必須</span>
            </label>
            <RadioRow>
              {(
                [
                  ["PRODUCER", "プロデューサー（責任者）"],
                  ["DIRECTOR", "プロデューサー"],
                  ["SPECIALIST", "スペシャリスト"],
                ] as const
              ).map(([value, label]) => (
                <RadioLabel key={value}>
                  <input
                    type="radio"
                    name="accept_position"
                    checked={form.accept_position === value}
                    onChange={() => update("accept_position", value)}
                  />
                  <span className="bodytext-13">{label}</span>
                </RadioLabel>
              ))}
            </RadioRow>
          </FieldFull>
          {form.accept_position === "SPECIALIST" && (
            <FieldFull>
              <Toggle
                value={form.allow_subcontracting}
                onClick={() =>
                  update("allow_subcontracting", !form.allow_subcontracting)
                }
              >
                <span className="bodytext-13">再委託を認める</span>
              </Toggle>
            </FieldFull>
          )}
        </Section>

        <Section>
          <SectionTitle>タグ</SectionTitle>
          {tagOptions.length === 0 ? (
            <Hint>登録されているロールタグがありません。</Hint>
          ) : (
            <TagSelector<number>
              tags={tagOptions}
              selectedIds={form.tags}
              onChange={(ids) =>
                setForm((prev) => ({ ...prev, tags: ids }))
              }
            />
          )}
        </Section>

        <Section>
          <FieldRow>
            <QuarterField>
              <Input
                label="納期 オフセット日数"
                option="required"
                type="number"
                value={form.deadline_offset_days}
                onChange={(e) =>
                  update("deadline_offset_days", e.target.value)
                }
              />
            </QuarterField>
            <QuarterField>
              <Input
                label="納期 時刻"
                option="required"
                type="time"
                value={form.deadline_time}
                onChange={(e) => update("deadline_time", e.target.value)}
              />
            </QuarterField>
            <QuarterField>
              <Input
                label="返答有効期限 日数"
                option="optional"
                type="number"
                value={form.valid_until_offset_days}
                onChange={(e) =>
                  update("valid_until_offset_days", e.target.value)
                }
              />
            </QuarterField>
            <QuarterField>
              <Input
                label="返答有効期限 時刻"
                option="optional"
                type="time"
                value={form.valid_until_time}
                onChange={(e) => update("valid_until_time", e.target.value)}
              />
            </QuarterField>
          </FieldRow>
          <FieldRow>
            <HalfField>
              <Input
                label="契約開始 オフセット日数 (空=null)"
                option="optional"
                type="number"
                value={form.contract_start_offset_days}
                onChange={(e) =>
                  update("contract_start_offset_days", e.target.value)
                }
              />
            </HalfField>
            <HalfField>
              <Input
                label="契約終了 オフセット日数 (空=null)"
                option="optional"
                type="number"
                value={form.contract_end_offset_days}
                onChange={(e) =>
                  update("contract_end_offset_days", e.target.value)
                }
              />
            </HalfField>
          </FieldRow>
          <FieldFull>
            <Textarea
              label="スコープ詳細 (scope)"
              option="optional"
              rows={3}
              value={form.scope}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                update("scope", e.target.value)
              }
            />
          </FieldFull>
          <FieldFull>
            <Textarea
              label="オファー本文の雛形 (initial_message)"
              option="optional"
              rows={4}
              value={form.initial_message}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                update("initial_message", e.target.value)
              }
            />
          </FieldFull>
          <FieldRow>
            <HalfField>
              <Input
                label="引渡場所"
                option="optional"
                value={form.delivery_location}
                onChange={(e) =>
                  update("delivery_location", e.target.value)
                }
              />
            </HalfField>
            <HalfField>
              <Input
                label="受け渡し方法"
                option="optional"
                value={form.delivery_method}
                onChange={(e) => update("delivery_method", e.target.value)}
              />
            </HalfField>
          </FieldRow>
        </Section>

        <Section>
          <SectionTitle>納品形式の候補 (deliverable_format_options)</SectionTitle>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) =>
              handleOptionDragEnd("deliverable_format_options", e)
            }
          >
            <SortableContext
              items={form.deliverable_format_options.map(
                (_, i) => `deliverable_format_options-${i}`,
              )}
              strategy={verticalListSortingStrategy}
            >
              {form.deliverable_format_options.map((opt, index) => (
                <SortableRow
                  key={`deliverable_format_options-${index}`}
                  id={`deliverable_format_options-${index}`}
                  onDelete={() =>
                    removeOption("deliverable_format_options", index)
                  }
                >
                  <div style={{ flex: 1 }}>
                    <Input
                      value={opt}
                      onChange={(e) =>
                        setOption(
                          "deliverable_format_options",
                          index,
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </SortableRow>
              ))}
            </SortableContext>
          </DndContext>
          <FullWidthAddBlock
            label="行を追加"
            onClick={() => addOption("deliverable_format_options")}
          />
        </Section>

        <Section>
          <SectionTitle>特記事項の候補 (special_provisions_options)</SectionTitle>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) =>
              handleOptionDragEnd("special_provisions_options", e)
            }
          >
            <SortableContext
              items={form.special_provisions_options.map(
                (_, i) => `special_provisions_options-${i}`,
              )}
              strategy={verticalListSortingStrategy}
            >
              {form.special_provisions_options.map((opt, index) => (
                <SortableRow
                  key={`special_provisions_options-${index}`}
                  id={`special_provisions_options-${index}`}
                  onDelete={() =>
                    removeOption("special_provisions_options", index)
                  }
                >
                  <div style={{ flex: 1 }}>
                    <Input
                      value={opt}
                      onChange={(e) =>
                        setOption(
                          "special_provisions_options",
                          index,
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </SortableRow>
              ))}
            </SortableContext>
          </DndContext>
          <FullWidthAddBlock
            label="行を追加"
            onClick={() => addOption("special_provisions_options")}
          />
        </Section>

        <Section>
          <SectionTitle>対価の内訳 (compensation_lines)</SectionTitle>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleCompensationDragEnd}
          >
            <SortableContext
              items={form.compensation_lines.map((_, i) => `comp-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              {form.compensation_lines.map((line, index) => (
                <SortableRow
                  key={`comp-${index}`}
                  id={`comp-${index}`}
                  onDelete={() => removeCompensationLine(index)}
                >
                  <div style={{ flex: 2 }}>
                    <Input
                      label="ラベル"
                      value={line.label}
                      onChange={(e) =>
                        setCompensationField(index, "label", e.target.value)
                      }
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <MoneyInput
                      label="単価"
                      suffix=""
                      value={line.unit_price}
                      onChange={(e) =>
                        setCompensationField(
                          index,
                          "unit_price",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <NumberInput
                      label="数量"
                      allowDecimal
                      value={line.quantity}
                      onChange={(e) =>
                        setCompensationField(
                          index,
                          "quantity",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Input
                      label="単位"
                      value={line.unit}
                      onChange={(e) =>
                        setCompensationField(index, "unit", e.target.value)
                      }
                    />
                  </div>
                </SortableRow>
              ))}
            </SortableContext>
          </DndContext>
          <FullWidthAddBlock
            label="行を追加"
            onClick={addCompensationLine}
          />
          <Hint>合計 (プレビュー): {formatYen(totalAmount)}</Hint>
        </Section>

        {error && <ErrorText>{error}</ErrorText>}
      </div>
    </Modal>
  );
};

export default RoleEditModal;
